import { type Auth } from 'firebase/auth';

import { useAuthInFlux } from '../auth/useAuthInFlux';
import { useAuthSnapshot } from './useAuthSnapshot';

/**
 * Resolves whether the live Firebase user is authenticated as a permanent
 * (non-anonymous) account. Originally inlined in `LandingPage.tsx`; lifted
 * out so the same auth-state read can drive the `<StickyPrimaryCta>`'s
 * sign-in slot on both the homepage and occasion pages (sheet bug #59 —
 * "sign-in" CTA still showing after login).
 *
 * Anon Firebase users are surfaced as `signedIn: false` — same as fully
 * signed-out users. Only permanent accounts return `signedIn: true`.
 *
 * `ready` flips from `false` to `true` once the first auth-state event
 * lands; consumers can use it to delay rendering decisions that would
 * otherwise flash the wrong state during the bootstrap.
 *
 * `ready` also stays `false` while auth is IN FLUX (`useAuthInFlux`): on
 * the mobile-OAuth redirect return, the first auth-state event delivers
 * the restored ANON session ~1-2s before `getRedirectResult` lands the
 * permanent user — so "ready + not signed in" would be a lie exactly when
 * the user just signed in, flashing the Sign-in CTA (reported 2026-07-06;
 * same class as sheet bug #59). Flux is bounded (grace window), so a lost
 * redirect degrades to the old behavior after ~4s, never a wedged CTA.
 *
 * `authInstance` is required and must be supplied by the caller (via
 * `useAuth()` from FirebaseContext, or a stub in stories/tests). Importing
 * `auth` from `firebaseConfig` here would trigger module-level Firebase
 * init, which crashes Storybook/Happo CI (no `.env.local` → undefined
 * config → `initializeApp` throws). See handbook gotcha #12.
 */
export type AuthOverride = 'signed-in' | 'signed-out' | 'loading' | undefined;

export interface IsSignedInResult {
  ready: boolean;
  signedIn: boolean;
}

export function useIsSignedIn(
  authOverride: AuthOverride,
  authInstance: Auth,
): IsSignedInResult {
  // Read permanent-ness via the shared snapshot (BOTH auth observers) so an
  // in-place anon→permanent upgrade — delivered only via onIdTokenChanged —
  // flips `signedIn` without depending on an incidental re-render (the prior
  // implementation stored the mutable User ref + onAuthStateChanged-only, which
  // recovered only because useAuthInFlux happened to re-render this hook).
  // Pass null under an override so the snapshot skips all subscription work; the
  // override branches below short-circuit before reading it.
  const snap = useAuthSnapshot(authOverride ? null : authInstance);
  // Called unconditionally (hooks rule); override paths below ignore it so
  // stories/tests stay fully deterministic.
  const inFlux = useAuthInFlux();

  if (authOverride === 'signed-in') return { ready: true, signedIn: true };
  if (authOverride === 'signed-out') return { ready: true, signedIn: false };
  if (authOverride === 'loading') return { ready: false, signedIn: false };
  return { ready: snap.ready && !inFlux, signedIn: snap.isPermanent };
}
